import React from "react";
import { Link } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { collection, getDocs, limit, orderBy, query, where } from "firebase/firestore";
import { Event as EventIcon } from "../icons";
import { LocalOffer as LocalOfferIcon } from "../icons";
import { DirectionsCar as DirectionsCarIcon } from "../icons";
import { HomeWork as HomeWorkIcon } from "../icons";
import { Storefront as StorefrontIcon } from "../icons";
import { db } from "../firebase";
import { T } from "../routes/Translate";
import { brandColors } from "../theme";

// Publicaciones que se pueden mencionar en un hilo escribiendo "#algo" (ver
// Hilos.js): eventos, ofertas, autos, propiedades y tiendas. Cada una se
// guarda en el hilo como { type, id, title, image } en el array `links`, y
// se muestra como tarjeta debajo del texto.
export const LINK_TYPES = {
  event: { label: "Evento", Icon: EventIcon, color: "#8E24AA", path: (id) => `/events/${id}` },
  offer: { label: "Oferta", Icon: LocalOfferIcon, color: "#2196F3", path: (id) => `/offers/${id}` },
  car: { label: "Auto", Icon: DirectionsCarIcon, color: "#43A047", path: (id) => `/cars/${id}` },
  place: { label: "Propiedad", Icon: HomeWorkIcon, color: "#00897B", path: (id) => `/trips/${id}` },
  store: { label: "Tienda", Icon: StorefrontIcon, color: "#E65100", path: (id) => `/stores/${id}` },
};

const firstImage = (images) => (Array.isArray(images) ? images[0] : images) || "";

// Una query por tipo; se trae una sola vez por sesión (la primera vez que se
// abren las sugerencias) y después se filtra en memoria mientras se escribe.
// Firestore no busca por "contiene", así que se traen las más recientes.
const SOURCES = [
  {
    type: "event",
    q: () => query(collection(db, "events"), where("isPrivate", "==", false), limit(300)),
    map: (d) => ({ title: d.title, image: firstImage(d.images) }),
  },
  {
    type: "offer",
    q: () => query(collection(db, "offers"), orderBy("createdAt", "desc"), limit(200)),
    map: (d) => ({ title: d.product || d.title, image: firstImage(d.images) }),
  },
  {
    type: "car",
    q: () => query(collection(db, "cars"), orderBy("createdAt", "desc"), limit(200)),
    map: (d) => ({ title: d.title, image: firstImage(d.images) }),
  },
  {
    type: "place",
    q: () => query(collection(db, "places"), orderBy("createdAt", "desc"), limit(200)),
    map: (d) => ({ title: d.title, image: firstImage(d.images) }),
  },
  {
    type: "store",
    q: () => query(collection(db, "stores"), limit(200)),
    map: (d) => ({ title: d.name || d.storeName, image: d.logoUrl || "" }),
  },
];

let catalogPromise = null;

const loadCatalog = () => {
  if (!catalogPromise) {
    catalogPromise = Promise.all(
      SOURCES.map(async ({ type, q, map }) => {
        try {
          const snap = await getDocs(q());
          return snap.docs
            .map((docSnap) => ({ type, id: docSnap.id, ...map(docSnap.data()) }))
            .filter((item) => item.title);
        } catch (error) {
          console.error(`Error al cargar ${type} para mencionar en hilos:`, error);
          return [];
        }
      })
    ).then((lists) => lists.flat());
  }
  return catalogPromise;
};

const normalize = (s) =>
  (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

// Publicaciones cuyo título contiene `term` (sin distinguir mayúsculas ni
// acentos); primero las que empiezan con el término.
export const searchLinkablePublications = async (term, max = 5) => {
  const needle = normalize(term.trim());
  if (!needle) return [];
  const catalog = await loadCatalog();
  return catalog
    .map((item) => ({ item, pos: normalize(item.title).indexOf(needle) }))
    .filter(({ pos }) => pos !== -1)
    .sort((a, b) => (a.pos === 0 ? 0 : 1) - (b.pos === 0 ? 0 : 1))
    .slice(0, max)
    .map(({ item }) => item);
};

// Fila usada en el popup de sugerencias del compositor.
export const LinkSuggestionRow = ({ item }) => {
  const { label, Icon, color } = LINK_TYPES[item.type];
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
      <Box sx={{ width: 30, height: 30, borderRadius: "8px", flexShrink: 0, bgcolor: color, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {item.image ? (
          <Box component="img" src={item.image} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <Icon sx={{ fontSize: 18, color: "#fff" }} />
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>{item.title}</Typography>
        <Typography variant="caption" sx={{ color, display: "flex", alignItems: "center", gap: 0.5 }}>
          <Icon sx={{ fontSize: 12 }} /> <T>{label}</T>
        </Typography>
      </Box>
    </Box>
  );
};

// Tarjetas de las publicaciones mencionadas en un hilo publicado.
const ThreadLinkCards = ({ links }) => {
  if (!Array.isArray(links) || links.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1 }}>
      {links.filter((l) => LINK_TYPES[l.type]).map((link) => {
        const { label, Icon, color, path } = LINK_TYPES[link.type];
        return (
          <Box
            key={`${link.type}-${link.id}`}
            component={Link}
            to={path(link.id)}
            sx={{
              display: "flex",
              alignItems: "stretch",
              borderRadius: "12px",
              border: `1px solid ${brandColors.divider}`,
              overflow: "hidden",
              textDecoration: "none",
              color: "inherit",
              "&:hover": { backgroundColor: brandColors.inputBackground },
            }}
          >
            <Box sx={{ width: 72, minHeight: 64, flexShrink: 0, bgcolor: color, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {link.image ? (
                <Box component="img" src={link.image} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <Icon sx={{ fontSize: 28, color: "#fff" }} />
              )}
            </Box>
            <Box sx={{ px: 1.5, py: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: brandColors.textPrimary }}>
                {link.title}
              </Typography>
              <Typography variant="caption" sx={{ color, display: "flex", alignItems: "center", gap: 0.5 }}>
                <Icon sx={{ fontSize: 13 }} /> <T>{label}</T>
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ThreadLinkCards;
